import { toast } from "react-toastify";
import { COLORS } from "assets/styles/colors";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { UAParser } from "ua-parser-js";

import { COUNTY_DETAILS, LOADING_STATUSES, RESIDENT_TYPES, SIGNATURE_TRANSFORM } from "./constants";

dayjs.extend(customParseFormat);

export const setStorage = (key, value) => {
  if (typeof value === "string") {
    localStorage.setItem(key, value);
  } else {
    localStorage.setItem(key, JSON.stringify(value));
  }
};

export const getStorage = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch (err) {
    return localStorage.getItem(key);
  }
};

export const isValidFileType = (files) => {
  if (!files || files.length === 0) {
    return false;
  }
  const validExtensions = ["jpg", "jpeg", "png", "xlsx", "pdf", "csv", "doc", "docx", "txt"];

  for (const file of Array.from(files)) {
    const fileName = file.name.split(".");
    const fileExtension = fileName[fileName.length - 1].toLowerCase();

    if (!validExtensions.includes(fileExtension)) {
      return false;
    }
  }
  return true;
};

export const removeStorage = (key) => localStorage.removeItem(key);

export const isEmpty = (object) => {
  if (Array.isArray(object)) {
    return !object.length;
  }

  if (typeof object === "object" && !Array.isArray(object) && object !== null) {
    return !Object.keys(object).length;
  }

  if (typeof object === "string") {
    return !object.length;
  }

  return true;
};

// Normalizes multi-value search input (account numbers, owner names, etc.) into the
// comma-separated string the backend expects, e.g. "123: 456" -> "123,456" and
// "123   456" -> "123,456", while keeping whitespace inside a name term intact,
// e.g. "Keegan Cohen" stays a single term instead of splitting into "Keegan","Cohen".
// Account numbers can be alphanumeric (e.g. "322595900D0250000"), so a word counts as an
// account number if it has at least one digit, not only if it's purely numeric.
const isAccountNumberWord = (word) => /^[A-Za-z0-9]+$/.test(word) && /\d/.test(word);

export const normalizeSearchQuery = (value) => {
  if (!value) return "";

  return String(value)
    .split(/[,;:\t\n]+/)
    .map((term) => term.trim())
    .filter(Boolean)
    .flatMap((term) => {
      const words = term.split(/\s+/).filter(Boolean);
      const isAllAccountNumbers = words.length > 1 && words.every(isAccountNumberWord);
      return isAllAccountNumbers ? words : [words.join(" ")];
    })
    .join(",");
};

// True once normalizeSearchQuery produced more than one term - the backend matches each
// comma-separated value independently, so a single sort column no longer applies.
export const isMultiValueSearch = (normalizedSearch) => normalizedSearch.includes(",");

export const getCountDetails = (details = "") => {
  return COUNTY_DETAILS[details.replace(/\s/g, "").toLowerCase()] || COUNTY_DETAILS.default;
};

export const generateAddressLink = (item = {}) => {
  if (item.county.toLowerCase() === "hays") {
    return getCountDetails(item.county)?.link?.url + item.account_number;
  }

  if (item.county.toLowerCase() === "rockwall") {
    return getCountDetails(item.county)?.link?.url + item.account_number + `/${new Date().getFullYear()}`;
  }

  if (item.county.toLowerCase() === "harris") {
    return getCountDetails(item.county)?.link?.url;
  }

  if (item.county.toLowerCase() === "elpaso") {
    return getCountDetails(item.county)?.link?.url + item.account_number + getCountDetails(item.county)?.link?.info;
  }

  if (item.county.toLowerCase() === "montgomery" && item.account_number) {
    const accountNumber = item.account_number.startsWith("R") ? item.account_number.substring(1) : item.account_number;
    return getCountDetails(item.county)?.link?.url + accountNumber + getCountDetails(item.county)?.link?.info;
  }

  if (item.county.toLowerCase() === "tarrant" && item.account_number) {
    const accountNumber = String(item.account_number).replace(/^0+/, "");
    return getCountDetails(item.county)?.link?.url + accountNumber + getCountDetails(item.county)?.link?.info;
  }

  if (item.county) {
    if (item.account_number) {
      return getCountDetails(item.county)?.link?.url + item.account_number + getCountDetails(item.county)?.link?.info;
    } else {
      return getCountDetails(item.county)?.link?.search_url;
    }
  }
};

export const preloadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve(img);
    };
    img.onerror = img.onabort = () => {
      reject(src);
    };
    img.src = src;
  });

export const snakeToCamel = (snakeString) => {
  let camelString = "";
  let isSnake = false;
  for (let i = 0; i < snakeString.length; i++) {
    if (snakeString[i] === "_") {
      isSnake = true;
    } else {
      if (isSnake) {
        camelString += snakeString[i].toUpperCase();
        isSnake = false;
      } else {
        camelString += snakeString[i];
      }
    }
  }

  return camelString;
};

export const getPaginationDescription = (paginationData, lastPage) => {
  return `${(paginationData.page - 1) * paginationData.rows + 1} - 
            ${
              paginationData.rows * paginationData.page >= lastPage
                ? lastPage
                : paginationData.rows * paginationData.page
            } of ${lastPage}`;
};

export const downloadFileByLink = (status, href, name = "properties.csv") => {
  if (status === LOADING_STATUSES.succeeded) {
    const link = document.createElement("a");
    link.href = href;
    link.setAttribute("download", name);
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  }
};

export const toggleClassOnRoot = () => document.getElementById("root").classList.toggle("prevent-scroll-desktop");

export const numberToDollar = (value) => {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });

  return formatter.format(value);
};

export const toastify = (type, message, className, timeout = 5000, position = "top-right") => {
  const toastId = toast[type](message, {
    position: position,
    hideProgressBar: true,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "colored",
    className: className,
    autoClose: false,
  });

  setTimeout(() => {
    toast.dismiss(toastId);
  }, timeout);
};

export const getFileType = (filename) => filename.split(".").pop().toLowerCase();

export const getImageFromTypedSignature = async ({ typedSignRef, signature, signType }) => {
  const { SCALE } = SIGNATURE_TRANSFORM;
  let result = signature;

  if (signType === "type") {
    await document.fonts.ready;
    await new Promise((res) => setTimeout(res, 100));

    const el = typedSignRef.current;
    const computed = window.getComputedStyle(el);
    const fontSize = parseFloat(computed.fontSize) || 40;
    const fontFamily = computed.fontFamily || "Italianno";
    const color = computed.color || "#000000";

    const scaledSize = fontSize * SCALE;
    const font = `${scaledSize}px ${fontFamily}`;

    const measure = document.createElement("canvas").getContext("2d");
    measure.font = font;
    const metrics = measure.measureText(signature);
    const textWidth = Math.ceil(metrics.width);
    const canvasHeight = Math.ceil(scaledSize * 1.5);

    const canvas = document.createElement("canvas");
    canvas.width = textWidth + 40;
    canvas.height = canvasHeight;

    const ctx = canvas.getContext("2d");
    ctx.font = font;
    ctx.fillStyle = color;
    ctx.textBaseline = "middle";
    ctx.fillText(signature, 20, canvasHeight / 2);

    result = canvas.toDataURL("image/png");
  }

  return result;
};

export function transformToClassicDate(date) {
  if (!date) return "";

  const parsedDate = dayjs(date, "YY-MM-DD");
  const day = parsedDate.date();
  const month = parsedDate.format("MMMM");

  if (!day) return "";

  let suffix = "th";

  if (day % 10 === 1 && day !== 11) suffix = "st";
  else if (day % 10 === 2 && day !== 12) suffix = "nd";
  else if (day % 10 === 3 && day !== 13) suffix = "rd";

  if (month === "Invalid Date") return "";

  return `${month} ${day}${suffix}`;
}

export const getSimplifiedAddress = (property) => {
  if (!property?.street_address) return "";

  let address = property.county_owner_address || property.street_address;
  [property.zipcode, property.zip, property.city, property.county_owner_state].forEach((value) => {
    if (typeof value === "string") {
      address = address.replace(new RegExp(`\\b${value}\\b`, "g"), "");
    }
  });

  return address.replace(/,/g, "").trim();
};

const deviceInfoParser = new UAParser();
const deviceInfo = deviceInfoParser.getResult();

export const getDeviceInfo = () => {
  // Native data
  const userAgent = navigator.userAgent;
  const screenSize = `${window.screen.width}x${window.screen.height}`;
  const viewport = `${window.innerWidth}x${window.innerHeight}`;
  const devicePixelRatio = window.devicePixelRatio;
  const language = navigator.language;

  // UAParser.js data
  const browser = `${deviceInfo.browser?.name} ${deviceInfo.browser?.version}`;
  const os = `${deviceInfo.os?.name} ${deviceInfo.os?.version}`;
  const deviceType = deviceInfo.device?.type || "Desktop";
  const deviceVendor = deviceInfo.device?.vendor || "Unknown";
  const deviceModel = deviceInfo.device?.model || "Unknown";

  const data = `
    User Agent: ${userAgent}
    Screen: ${screenSize}
    Viewport: ${viewport}
    Pixel Ratio: ${devicePixelRatio}
    Language: ${language}

    Browser: ${browser}
    OS: ${os}
    Device Type: ${deviceType}
    Device Vendor: ${deviceVendor}
    Device Model: ${deviceModel}
`;

  return data.trim();
};

export function areAllSavedFiltersEmpty(filters) {
  for (const key in filters) {
    if (Array.isArray(filters[key]) && filters[key].length > 0) {
      return false;
    }
    if (filters[key] === null) {
      continue;
    }
    if (typeof filters[key] === "object" && filters[key] !== null) {
      const hasNonEmptyValue = Object.values(filters[key]).some(
        (value) => (Array.isArray(value) && value.length > 0) || (value !== null && value !== "")
      );
      if (hasNonEmptyValue) {
        return false;
      }
    } else if (filters[key] !== "" && filters[key] !== null) {
      return false;
    }
  }
  return true;
}

export const generateParams = ({
  property_id,
  sortData,
  residentType,
  appliedFilters,
  selects,
  ranges,
  dates,
  floats,
}) => {
  const params = {
    is_sale: residentType === RESIDENT_TYPES.equity ? 0 : 1,
    property_id,
    order_column: sortData.field,
    direction: sortData.direction === 1 ? "ASC" : "DESC",
  };

  params.is_filtered = areAllSavedFiltersEmpty(appliedFilters) ? 0 : 1;

  selects?.forEach((selectItem) => {
    const key = selectItem?.description;
    let value = appliedFilters?.[key];

    if (key === "market_area_code" && value) {
      value = value.map((option) => option.split(" - ")[0]);
    }

    if (value && value.length > 0) {
      params[key] = value;
    }
  });

  ranges?.forEach((range) => {
    const key = range?.description;
    let value = appliedFilters?.[key];

    if (value && typeof value === "object" && "start" in value && "end" in value) {
      const start = value.start !== -Infinity && value.start !== "" && value.start !== null ? value.start : undefined;
      const end = value.end !== Infinity && value.end !== "" && value.end !== null ? value.end : undefined;

      if ((start !== undefined && start !== null) || (end !== undefined && end !== null)) {
        params[key] = {
          ...(start !== undefined ? { start } : {}),
          ...(end !== undefined ? { end } : {}),
        };
      }
    } else if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }
  });

  dates?.forEach((date) => {
    const key = date?.description;
    let value = appliedFilters?.[key];

    if (value && typeof value === "object" && "start" in value && "end" in value) {
      const start = value.start !== -Infinity && value.start !== "" && value.start !== null ? value.start : undefined;
      const end = value.end !== Infinity && value.end !== "" && value.end !== null ? value.end : undefined;

      if ((start !== undefined && start !== null) || (end !== undefined && end !== null)) {
        params[key] = {
          ...(start !== undefined ? { start } : {}),
          ...(end !== undefined ? { end } : {}),
        };
      }
    } else if (value !== undefined && value !== null && value !== "") {
      params[key] = value;
    }
  });

  floats?.forEach((float) => {
    const key = float?.description;
    let value = appliedFilters?.[key];

    if (value) {
      params[key] = value;
    }
  });

  return params;
};

export const resolvePrintFileName = ({ template, county, accountNumber }) => {
  const pad = (n) => String(n).padStart(2, "0");
  const now = new Date();
  const year = String(now.getFullYear());
  const datetime = `${year}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(
    now.getMinutes()
  )}-${pad(now.getSeconds())}`;
  const raw = (template && template.trim() ? template : "{account_number}")
    .replaceAll("{county}", county ?? "")
    .replaceAll("{year}", year)
    .replaceAll("{datetime}", datetime)
    .replaceAll("{account_number}", accountNumber ?? "");
  const safe = raw.replace(/[^A-Za-z0-9._-]+/g, "_").replace(/^_+|_+$/g, "") || String(accountNumber ?? "file");
  return `${safe}.pdf`;
};

// Back-compat wrapper: keep the old signature working with the default template.
export const getPrintFileName = (accountNumber, packetCounty) =>
  resolvePrintFileName({ template: "{account_number}", county: packetCounty, accountNumber });

export const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  return `${unitIndex === 0 ? size : size.toFixed(1)} ${units[unitIndex]}`;
};

/**
 * True when a comp's market value sits below the notice value, which is what the red triangle in
 * the market value column marks and what the Only Dynamic Equity toggle filters on.
 *
 * Shared so the two can never disagree. They were written separately and the filter was defeated by
 * the flagged re-add just below it, which put triangle-less rows back on screen while the toggle was
 * on and made it look broken.
 */
export const isBelowNoticeValue = (item) => {
  const marketValue = Number(item?.certified_market_value);
  const noticeValue = Number(item?.notice_value);

  return Boolean(marketValue && noticeValue && marketValue < noticeValue);
};

/**
 * Colour for the evidence sidebar "+/-" row. The backend returns it as savings (base - adjusted), so a
 * negative number means the adjusted value landed ABOVE the subject's base. It used to render green
 * regardless of sign, which made an increase read as a win at a glance.
 */
export const deltaColor = (value) => (Number(value) < 0 ? COLORS.red_400 : COLORS.green_300);
